/**
 * Edits to the rail layout: sections, spaces, sub-items, and one-step moves.
 * Pure — every function takes a layout and returns a new one, unchanged in
 * content when the edit does not apply.
 *
 * Spec: docs/plans/2026-09-16-nav-rail-sections-design.md §1 (revisions 2 and 3).
 */
import {
  childrenOf,
  cleanLabel,
  containerKind,
  containerOf,
  idOf,
  indexOfItem,
  isParent,
  isRow,
  isSection,
  itemsIn,
  moveWithin,
  NEW_SECTION_LABEL,
  ROOT,
  starsOf,
  topIndexOf,
  updateContainer,
  withChildren,
  type RailItem,
  type RailLayout,
  type RowEntry,
} from './railLayout';

/** A fresh id with a prefix: `s-` for a section, `sp-` for a space. */
export function newItemId(prefix: 's' | 'sp'): string {
  return `${prefix}-${crypto.randomUUID().slice(0, 8)}`;
}

/** `layout` without item `id`, and the item taken (with its sub-items or rows). */
export function takeItem(
  layout: RailLayout,
  id: string
): { layout: RailLayout; item: RailItem | undefined } {
  const key = containerOf(layout, id);
  if (key === undefined) return { layout, item: undefined };
  const item = itemsIn(layout, key).find(entry => idOf(entry) === id);
  return { layout: updateContainer(layout, key, items => items.filter(e => idOf(e) !== id)), item };
}

/** `layout` with `item` inserted into container `key` at `index` (the end when out of range). */
export function insertItem(
  layout: RailLayout,
  key: string,
  item: RailItem,
  index = -1
): RailLayout {
  return updateContainer(layout, key, items => {
    const next = [...items];
    next.splice(index < 0 || index > next.length ? next.length : index, 0, item);
    return next;
  });
}

/**
 * Add a section. With no `beforeId` it is empty and goes at the end. Above a
 * row, the new section takes that row (with its sub-items) and sits where the
 * row's top-level item sat.
 */
export function addSection(
  layout: RailLayout,
  label: string,
  beforeId?: string,
  id: string = newItemId('s')
): { layout: RailLayout; id: string } {
  const section = { id, label: cleanLabel(label) || NEW_SECTION_LABEL, ids: [] as RowEntry[] };
  const at = beforeId === undefined ? -1 : topIndexOf(layout, beforeId);
  if (beforeId === undefined || at === -1) return { layout: [...layout, section], id };
  const taken = takeItem(layout, beforeId);
  const row = taken.item !== undefined && isRow(taken.item) ? taken.item : beforeId;
  // Taking a top-level row out removes its slot, so the section lands at `at` either way.
  return { layout: insertItem(taken.layout, ROOT, { ...section, ids: [row] }, at), id };
}

/** Add a space at the end, or just above the top-level item holding `beforeId`. */
export function addSpace(
  layout: RailLayout,
  beforeId?: string,
  id: string = newItemId('sp')
): RailLayout {
  const at = beforeId === undefined ? -1 : topIndexOf(layout, beforeId);
  return insertItem(layout, ROOT, { kind: 'space', id }, at);
}

/** Remove a space. */
export function removeSpace(layout: RailLayout, id: string): RailLayout {
  return layout.filter(item => idOf(item) !== id);
}

/** Rename a section; an empty label leaves the old one. */
export function renameSection(layout: RailLayout, id: string, label: string): RailLayout {
  const clean = cleanLabel(label);
  if (!clean) return [...layout];
  return layout.map(item => (isSection(item) && item.id === id ? { ...item, label: clean } : item));
}

/** Remove a section; its rows stay where they were, ungrouped. */
export function removeSection(layout: RailLayout, id: string): RailLayout {
  return layout.flatMap(item => (isSection(item) && item.id === id ? item.ids : [item]));
}

/** Whether top-level item `id` can move `delta` places. */
export function canMoveItem(layout: RailLayout, id: string, delta: number): boolean {
  const from = indexOfItem(layout, id);
  return from !== -1 && from + delta >= 0 && from + delta < layout.length;
}

/** Move a top-level item — a section with its rows, a space, or a row — `delta` places. */
export function moveItem(layout: RailLayout, id: string, delta: number): RailLayout {
  if (!canMoveItem(layout, id, delta)) return [...layout];
  const from = indexOfItem(layout, id);
  return moveWithin(layout, from, from + delta);
}

/** Whether row `id` can take one step in direction `delta` (-1 up, 1 down). */
export function canMoveEntry(layout: RailLayout, id: string, delta: number): boolean {
  const key = containerOf(layout, id);
  if (key === undefined) return false;
  const kind = containerKind(layout, key);
  if (kind === 'section') return true;
  const items = itemsIn(layout, key);
  const to = items.findIndex(item => idOf(item) === id) + delta;
  return to >= 0 && to < items.length;
}

/** One step for a row inside section `key`: within it, or out past its edge. */
function stepInSection(layout: RailLayout, key: string, id: string, delta: number): RailLayout {
  const rows = itemsIn(layout, key);
  const at = rows.findIndex(entry => idOf(entry) === id);
  if (at + delta >= 0 && at + delta < rows.length) {
    return updateContainer(layout, key, items => moveWithin(items, at, at + delta));
  }
  const taken = takeItem(layout, id);
  const sectionAt = indexOfItem(taken.layout, key);
  return insertItem(taken.layout, ROOT, taken.item ?? id, delta < 0 ? sectionAt : sectionAt + 1);
}

/**
 * One step for a row. A sub-item moves among its siblings. In a section a row
 * moves among the section's rows and steps out past the first or last. At the
 * top level a row swaps with a neighbouring row or space, or steps into a
 * neighbouring section at its near end. A parent always carries its sub-items.
 */
export function moveEntry(layout: RailLayout, id: string, delta: number): RailLayout {
  if (!canMoveEntry(layout, id, delta)) return [...layout];
  const key = containerOf(layout, id) ?? ROOT;
  const kind = containerKind(layout, key);
  if (kind === 'section') return stepInSection(layout, key, id, delta);
  const items = itemsIn(layout, key);
  const from = items.findIndex(item => idOf(item) === id);
  const neighbour = items[from + delta];
  if (kind === 'children' || neighbour === undefined || !isSection(neighbour)) {
    return updateContainer(layout, key, list => moveWithin(list, from, from + delta));
  }
  const taken = takeItem(layout, id);
  return insertItem(taken.layout, neighbour.id, taken.item ?? id, delta < 0 ? -1 : 0);
}

/** The row just above `id` in its own container, if that is a row. */
export function rowAbove(layout: RailLayout, id: string): RowEntry | undefined {
  const key = containerOf(layout, id);
  if (key === undefined) return undefined;
  const items = itemsIn(layout, key);
  const above = items[items.findIndex(item => idOf(item) === id) - 1];
  return above !== undefined && isRow(above) ? above : undefined;
}

/**
 * Whether row `id` can become a sub-item of the row above it. Only a plain row
 * at the top level or in a section qualifies — sub-items go one level deep, so a
 * sub-item and a parent cannot — and the item above must be a row.
 */
export function canIndent(layout: RailLayout, id: string): boolean {
  const key = containerOf(layout, id);
  if (key === undefined || containerKind(layout, key) === 'children') return false;
  const entry = itemsIn(layout, key).find(item => idOf(item) === id);
  return entry !== undefined && !isParent(entry) && rowAbove(layout, id) !== undefined;
}

/** Make row `id` the last sub-item of the row above it. */
export function indent(layout: RailLayout, id: string): RailLayout {
  const above = rowAbove(layout, id);
  if (!canIndent(layout, id) || above === undefined) return [...layout];
  const aboveId = idOf(above);
  const key = containerOf(layout, id) ?? ROOT;
  return updateContainer(layout, key, items =>
    items.flatMap(item => {
      if (idOf(item) === id) return [];
      return idOf(item) === aboveId ? [withChildren(aboveId, [...childrenOf(above), id])] : [item];
    })
  );
}

/** The parent row holding sub-item `id`, if it is one. */
export function parentOf(layout: RailLayout, id: string): string | undefined {
  const key = containerOf(layout, id);
  return key !== undefined && containerKind(layout, key) === 'children' ? key : undefined;
}

/** Move sub-item `id` out of its parent, to just below the parent. */
export function outdent(layout: RailLayout, id: string): RailLayout {
  const parent = parentOf(layout, id);
  if (parent === undefined) return [...layout];
  const withoutChild = updateContainer(layout, parent, items => items.filter(e => e !== id));
  const holder = containerOf(withoutChild, parent) ?? ROOT;
  const at = itemsIn(withoutChild, holder).findIndex(item => idOf(item) === parent) + 1;
  return insertItem(withoutChild, holder, id, at);
}

/** Unstar a destination wherever it is; a parent's sub-items stay, in its place. */
function unstar(layout: RailLayout, id: string): RailLayout {
  const key = containerOf(layout, id);
  if (key === undefined) return [...layout];
  return updateContainer(layout, key, items =>
    items.flatMap(item => {
      if (idOf(item) !== id) return [item];
      return isRow(item) ? childrenOf(item) : [];
    })
  );
}

/** Star (append ungrouped at the end) or unstar a destination. */
export function toggleStar(layout: RailLayout, id: string): RailLayout {
  return starsOf(layout).includes(id) ? unstar(layout, id) : [...layout, id];
}
